import { useState, useEffect } from 'react';
import { getSupportLanguages } from '@/api/customer';
import Constants from '@/constants/const';

interface Language {
  key: string;
  label: string;
}

/**
 * 纯净版的 useSupportLanguages Hook
 *
 * 1. 如果外部提供了 providedLanguages，直接使用。
 * 2. 否则，执行 API 调用获取数据。
 */
export function useSupportLanguages(providedLanguages?: Language[]) {
  const [localLanguages, setLocalLanguages] = useState<Language[]>([]);
  const [localLoading, setLocalLoading] = useState<boolean>(false);

  useEffect(() => {
    // 优化：只有当外部完全没有提供该属性（undefined）且本地也没有数据时，才发起请求。
    // 如果传入了 []，说明调用者正在负责管理这个状态，Hook 应该保持静默。
    if (providedLanguages !== undefined || localLanguages.length > 0) return;

    const fetchLanguages = async () => {
      setLocalLoading(true);
      try {
        const { object = {} } = await getSupportLanguages();
        const languages = (object.language || []).map(item => {
          if (item.key === Constants.LANGUAGE_CHINESE_T_ALIAS) {
            return {
              ...item,
              key: Constants.LANGUAGE_CHINESE_T
            };
          }
          return item;
        });
        setLocalLanguages(languages);
      } catch (error) {
        console.error('Failed to fetch support languages', error);
        setLocalLanguages([]);
      } finally {
        setLocalLoading(false);
      }
    };

    fetchLanguages();
  }, [providedLanguages, localLanguages.length]);

  // 如果外部传了，优先用外部的
  if (providedLanguages && providedLanguages.length > 0) {
    return {
      languages: providedLanguages,
      loading: false
    };
  }

  // 否则返回本地获取的（或者是初始的空列表）
  return {
    languages: localLanguages,
    loading: localLoading
  };
}
